import { Check, LoaderCircle, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";

type NetworkPolicy = { outboundHosts: string[]; providerHosts: string[] };

function parseHosts(value: string) {
  return [
    ...new Set(
      value
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

export function NetworkPolicyPage() {
  const [outboundDraft, setOutboundDraft] = useState("");
  const [providerDraft, setProviderDraft] = useState("");
  const [busy, setBusy] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void api<NetworkPolicy>("/admin/network-policy")
      .then((next) => {
        setOutboundDraft(next.outboundHosts.join("\n"));
        setProviderDraft(next.providerHosts.join("\n"));
      })
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : "Network policy could not be loaded",
        ),
      )
      .finally(() => setBusy(false));
  }, []);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      const next = await api<NetworkPolicy>("/admin/network-policy", {
        method: "PATCH",
        body: JSON.stringify({
          outboundHosts: parseHosts(outboundDraft),
          providerHosts: parseHosts(providerDraft),
        }),
      });
      setOutboundDraft(next.outboundHosts.join("\n"));
      setProviderDraft(next.providerHosts.join("\n"));
      setSaved(true);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Network policy could not be saved",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsPage
      title={t("Network policy")}
      description={t(
        "Manage the site-approved public destinations available to organizations. Private networks and arbitrary hosts remain blocked.",
      )}
    >
      <section className="panel max-w-3xl p-5 sm:p-6">
        <label>
          <span className="label">{t("Approved outbound hosts")}</span>
          <textarea
            className="field min-h-28 font-mono text-sm"
            disabled={busy}
            value={outboundDraft}
            onChange={(event) => {
              setOutboundDraft(event.target.value);
              setSaved(false);
            }}
          />
          <span className="mt-2 block text-xs text-stone-500">
            {t(
              "One public hostname per line. This is the site-wide maximum catalog; assign each organization's subset from Site administration → Organizations → Security policy.",
            )}
          </span>
        </label>
        <label className="mt-5 block">
          <span className="label">{t("Approved provider hosts")}</span>
          <textarea
            className="field min-h-24 font-mono text-sm"
            disabled={busy}
            value={providerDraft}
            onChange={(event) => {
              setProviderDraft(event.target.value);
              setSaved(false);
            }}
          />
          <span className="mt-2 block text-xs text-stone-500">
            {t("Provider hosts must also be present in the outbound catalog.")}
          </span>
        </label>
        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">{error}</p>
        )}
        <div className="mt-5 flex justify-end">
          <button
            className="button-primary"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : saved ? (
              <Check size={16} />
            ) : (
              <Save size={16} />
            )}{" "}
            {saved ? t("Saved") : t("Save")}
          </button>
        </div>
      </section>
    </SettingsPage>
  );
}
